import React, { useState } from 'react'
import { Card, Tabs, Alert } from 'antd'
import { useTranslation } from 'react-i18next'
import { PageLoading } from '@/shared/components/ui'
import { useAuthReady } from '@/shared/hooks/useAuthReady'
import { UserRole } from '@/types/auth.types'
import OrderQueryPage from './OrderQueryPage'
import OrderConfigPage from './OrderConfigPage'
import './Order.scss'

const OrderPage: React.FC = () => {
  const { t } = useTranslation(['modules', 'common'])
  const { isReady, companyId, user } = useAuthReady()
  const [activeTab, setActiveTab] = useState('query')

  // 权限检查（使用统一角色枚举）
  const isCompanyAdmin = user?.role === UserRole.COMPANY_ADMIN
  const isRegularUser = user?.role === UserRole.USER
  // 注意：原seller_agent角色已移除，相关权限由company_admin承担

  // 标签页变更处理
  const handleTabChange = (key: string) => {
    setActiveTab(key)
  }

  // 权限不足时显示的消息
  const PermissionDenied: React.FC<{ message: string }> = ({ message }) => {
    const { t } = useTranslation(['modules', 'common']);
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <h3 style={{ color: '#ff4d4f', marginBottom: '16px' }}>
            {t('modules:order.permission.denied', '⚠️ 权限不足')}
          </h3>
          <p style={{ color: '#666' }}>{message}</p>
        </div>
      </Card>
    );
  }

  if (!isReady) {
    return <PageLoading />
  }

  // 状态恢复完成但companyId为空（异常情况）
  if (!companyId) {
    return (
      <Card>
        <Alert
          message={t('modules:order.noCompanyError', '无法获取公司信息')}
          description={t('modules:order.noCompanyErrorDesc', '请重新登录或联系管理员')}
          type="error"
          showIcon
        />
      </Card>
    )
  }

  // Tabs items 配置
  const tabItems = [
    {
      key: 'query',
      label: t('modules:order.tabs.query'),
      disabled: false,
      children: (
        <OrderQueryPage
          companyId={companyId}
          readOnly={isRegularUser}
          canUpdateStatus={isCompanyAdmin}
          canAddNotes={isCompanyAdmin}
          canExport={true}
        />
      )
    },
    {
      key: 'config',
      label: t('modules:order.tabs.config'),
      disabled: !isCompanyAdmin,
      children: isCompanyAdmin ? (
        <OrderConfigPage companyId={companyId} />
      ) : (
        <PermissionDenied message={t('modules:order.permission.configAdminOnly')} />
      )
    }
  ]

  return (
    <div className="order-page">
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        size="large"
        tabBarStyle={{ marginBottom: 24 }}
        items={tabItems}
      />
    </div>
  )
}

export default OrderPage